/**
 * Display copy for the generate page's controls.
 *
 * Every *value* here is typed from the generated schema, so a tier or context added to the
 * backend breaks this file at compile time rather than rendering as a raw slug. Every *label
 * and hint* is UI copy, which the domain layer deliberately does not carry — the same split
 * as `messages.ts` and `brand.ts`, neither of which has a Python counterpart.
 *
 * The guardrail's copy is the exception and is **not** here: it is served from
 * `/guardrail/config`, because the exact warning wording is specified by the client's brief
 * and appears verbatim both on screen and in the server's rejection message. See
 * `src/guardrail/rules.ts`.
 */

import type { components } from "@/api/schema";

type Schemas = components["schemas"];

export type GenerateMode = Schemas["GenerationMode"];
export type GenerationTier = Schemas["GenerationTier"];
export type PublicationContext = Schemas["PublicationContext"];
export type LightingPreference = Schemas["LightingPreference"];
export type Weather = Schemas["Weather"];
export type CreativeTerritory = Schemas["CreativeTerritory"];
export type Ratio = Schemas["Ratio"];
export type PrintSizeKey = Schemas["PrintSizeKey"];
export type OutputType = Schemas["OutputType"];
export type IntendedLayout = Schemas["IntendedLayout"];
export type ImageRecommendation = Schemas["ImageRecommendation"];

/**
 * The portrait-framing control's values: "auto" plus the three portrait `SubjectType`s.
 *
 * A dedicated control rather than the full nine-value list — product, landscape and macro
 * have no reason to appear as buttons and are inferred from the brief. Edit mode only: a
 * framing choice needs a real, already-supplied photograph of a subject to apply to.
 */
export type PortraitFraming =
  | "auto"
  | Extract<
      Schemas["SubjectType"],
      "environmental-portrait" | "close-portrait" | "full-body-portrait"
    >;

// ── Mode ─────────────────────────────────────────────────────────────────────────────────

/**
 * The page's primary fork: which of the two ways of producing an image this is.
 *
 * Carries a `hint` because it is now rendered as a pair of cards rather than a pill pair —
 * it decides which form, which endpoint and which half of the page's remaining controls are
 * shown, so the two paths are worth making legible *before* one is picked rather than after.
 */
export const MODE_OPTIONS: ReadonlyArray<{ value: GenerateMode; label: string; hint: string }> = [
  {
    value: "describe",
    label: "Describe it",
    hint: "Write a brief and generate a new image from scratch.",
  },
  {
    value: "edit",
    label: "Supply an image",
    hint: "Upload a photo you already have and say how it should change.",
  },
];

// ── Shape and output ─────────────────────────────────────────────────────────────────────

export const RATIO_OPTIONS: ReadonlyArray<{ value: Ratio; label: string; size: string }> = [
  { value: "portrait", label: "Portrait", size: "1024x1536" },
  { value: "landscape", label: "Landscape", size: "1536x1024" },
  { value: "square", label: "Square", size: "1024x1024" },
];

export const OUTPUT_TYPE_OPTIONS: ReadonlyArray<{
  value: OutputType;
  label: string;
  hint: string;
}> = [
  { value: "digital", label: "Digital", hint: "PNG at screen resolution: web, slides, social." },
  {
    value: "print",
    label: "Print",
    hint: "High-quality JPEG at 300 DPI, sized for a physical print.",
  },
];

export const PRINT_SIZE_OPTIONS: readonly PrintSizeKey[] = ["4x6", "5x7", "8x10", "a4", "a3"];

/** The print picker's default — the most common physical print size. */
export const DEFAULT_PRINT_SIZE: PrintSizeKey = "8x10";

// ── Generation tier ──────────────────────────────────────────────────────────────────────

/** Edit mode's valid tiers — a real photo as the base, varying in how much may change. */
export const EDIT_GENERATION_TIERS: readonly GenerationTier[] = [
  "strict-preservation",
  "generative-enhancement",
];

/** Describe mode's — nothing real to preserve, varying in how far from photorealism. */
export const DESCRIBE_GENERATION_TIERS: readonly GenerationTier[] = [
  "creative-composition",
  "conceptual-illustrative",
];

export const GENERATION_TIER_LABELS: Record<GenerationTier, string> = {
  "strict-preservation": "Strict preservation",
  "generative-enhancement": "Generative enhancement",
  "creative-composition": "Realistic scene",
  "conceptual-illustrative": "Symbolic concept",
};

export const GENERATION_TIER_HINTS: Record<GenerationTier, string> = {
  "strict-preservation":
    "Technical correction only — the real subject, framing and everything already visible stays exactly as it is.",
  "generative-enhancement":
    "The AI may generatively change more than a technical correction — background, lighting, composition.",
  "creative-composition": "A new, photorealistic scene composed from your brief.",
  "conceptual-illustrative":
    "A symbolic or conceptual composition, clearly not a literal document of a real event — still must read as a photograph, not a drawing.",
};

/**
 * Each mode's default tier, chosen so the control changes no existing behaviour: describe
 * mode's plain photorealistic synthesis already *is* "creative composition", and edit mode's
 * unconstrained instruction already is "generative enhancement". Only picking the other tier
 * changes what reaches the model.
 */
export function defaultGenerationTierFor(mode: GenerateMode): GenerationTier {
  return mode === "describe" ? "creative-composition" : "generative-enhancement";
}

// ── Variations ───────────────────────────────────────────────────────────────────────────

/**
 * Capped at 4 rather than left open-ended: each variation is a full extra billed image and a
 * full extra rate-limit slot, so an unbounded value would let one request silently consume a
 * caller's whole hourly window.
 */
export const VARIATION_OPTIONS: readonly number[] = [1, 2, 3, 4];
export const DEFAULT_VARIATIONS = 1;

/**
 * Only offered once more than one variation is requested — with a single image there is
 * nothing to explore different directions *from* or refine *within*. The third
 * `CreativeTerritory` value, "perfect", is never a choice made here: it is simply what one
 * variation already is, derived rather than offered as a redundant fourth option.
 */
export const VARIATION_DIRECTION_OPTIONS: ReadonlyArray<{
  value: Extract<CreativeTerritory, "explore" | "refine">;
  label: string;
  hint: string;
}> = [
  {
    value: "explore",
    label: "Explore different directions",
    hint: "Each option is a genuinely different take — different framing, angle and mood — not a re-roll of the same one.",
  },
  {
    value: "refine",
    label: "Refine one direction",
    hint: "Each option is a close variation of the same direction — small differences in detail.",
  },
];

// ── Photographic direction ───────────────────────────────────────────────────────────────

/**
 * "Subject in setting" rather than "Environmental" (the underlying `SubjectType`'s own name):
 * for this app's audience "environmental" reads as ESG — the exact category the guardrail on
 * the same page exists to flag — not as a photography term.
 */
export const PORTRAIT_FRAMING_OPTIONS: ReadonlyArray<{
  value: PortraitFraming;
  label: string;
  hint: string;
}> = [
  { value: "auto", label: "Not a portrait / Auto", hint: "Let the brief decide the subject and framing." },
  {
    value: "environmental-portrait",
    label: "Subject in setting",
    hint: "Subject and their surroundings both in view, not candid or action, just wider framing.",
  },
  { value: "close-portrait", label: "Close-up", hint: "Head-and-shoulders or tighter, shallow depth of field." },
  { value: "full-body-portrait", label: "Full-body", hint: "The whole figure, head to feet." },
];

/**
 * "Auto" is a real choice, not "nothing selected": it tells the prompt builder to describe
 * only what the permanent realism layer already requires — one consistent, plausible light
 * source — without presuming a time of day the user never asked for.
 */
export const LIGHTING_OPTIONS: ReadonlyArray<{
  value: LightingPreference;
  label: string;
  hint: string;
}> = [
  { value: "auto", label: "Auto", hint: "Let the brief decide: no time of day assumed." },
  { value: "golden-hour", label: "Golden hour", hint: "Warm, low-angle sunlight with long shadows." },
  { value: "daylight", label: "Daylight", hint: "Natural, neutral daylight." },
  { value: "overcast", label: "Overcast", hint: "Soft, even light with subdued contrast." },
  { value: "night", label: "Night", hint: "Practical light sources, real high-ISO grain, no day-for-night look." },
  { value: "studio", label: "Studio", hint: "A controlled key / fill / rim studio setup." },
  { value: "practical", label: "Practical", hint: "Lit by the scene's own lamps, windows or signage." },
];

/** "Auto" means no condition assumed, not "clear skies". */
export const WEATHER_OPTIONS: ReadonlyArray<{ value: Weather; label: string; hint: string }> = [
  { value: "auto", label: "Auto", hint: "No weather condition assumed." },
  { value: "sunny", label: "Sunny", hint: "Clear sky, crisp defined shadows." },
  { value: "rain", label: "Rain", hint: "Wet reflective surfaces, softer diffused light." },
  { value: "snow", label: "Snow", hint: "Snow-covered ground, cool ambient bounce light." },
  { value: "fog", label: "Fog", hint: "Reduced contrast and visibility with distance." },
  { value: "storm", label: "Storm", hint: "Heavy cloud cover, darker light, wind-driven movement." },
];

// ── Publication context ──────────────────────────────────────────────────────────────────

export const PUBLICATION_CONTEXT_LABELS: Record<PublicationContext, string> = {
  unspecified: "Not specified",
  "annual-report": "Annual report",
  "sustainability-report": "Sustainability / ESG report",
  "corporate-website": "Corporate website",
  "recruitment-campaign": "Recruitment campaign",
  "investor-presentation": "Investor presentation",
  "social-media": "Social media",
  "internal-communications": "Internal communications",
  advertising: "Advertising",
  "editorial-news": "Editorial / news",
};

export const PUBLICATION_CONTEXT_OPTIONS = Object.keys(
  PUBLICATION_CONTEXT_LABELS,
) as PublicationContext[];

// ── Campaign context panel ───────────────────────────────────────────────────────────────

export const INTENDED_LAYOUT_LABELS: Record<IntendedLayout, string> = {
  "full-bleed-hero": "Full-bleed hero",
  "inset-supporting": "Inset / supporting",
  thumbnail: "Thumbnail",
  unsure: "Not sure yet",
};

export const INTENDED_LAYOUT_OPTIONS = Object.keys(INTENDED_LAYOUT_LABELS) as IntendedLayout[];

export const IMAGE_RECOMMENDATION_LABELS: Record<ImageRecommendation, string> = {
  documentary: "Documentary photograph",
  conceptual: "Conceptual image",
  "operational-detail": "Operational detail",
  "human-story": "Human story",
  "data-visual": "Data visual",
  illustration: "Illustration",
  "no-image": "No image needed",
};

// ── Export presets ───────────────────────────────────────────────────────────────────────

/**
 * A curated set of real platform placements rather than an open-ended aspect-ratio input.
 * The keys must match `app/domain/export/crop_presets.py`; the labels are this app's copy.
 */
export const CROP_PRESET_OPTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "square", label: "Square post" },
  { key: "portrait-feed", label: "Portrait feed" },
  { key: "story", label: "Story / Reel" },
  { key: "landscape-post", label: "Landscape post" },
  { key: "link-banner", label: "Link preview banner" },
];

// ── The "just enhance it" quick action ───────────────────────────────────────────────────

/**
 * A fixed instruction a user can drop into the edit textarea instead of writing their own.
 * No model call — the text never varies.
 *
 * The wording is deliberately strict about preserving reality, and repeats the constraint
 * from several angles: this app's guardrails exist because AI imagery can misrepresent real
 * people and events, and an "enhance" action that quietly let the model invent content would
 * undermine them. A single soft mention is easy for an image model to drift past, especially
 * on a feature aimed at users who will not proof the wording themselves. It still lands in an
 * editable textarea, so nothing here is hidden.
 */
export const ENHANCE_INSTRUCTION =
  "Enhance this exact photograph to professional editorial quality, do not reimagine it. " +
  "Keep the real subject, their likeness, expression, pose and clothing, the physical " +
  "setting, every object, and any visible text or logos completely unchanged: add nothing, " +
  "remove nothing, invent nothing. Apply only professional post-production finishing: " +
  "correct exposure and white balance, improve color accuracy and tonal range, increase " +
  "sharpness and clarity, reduce noise, and balance contrast: the level of retouching a " +
  "skilled editorial photographer would give a client-ready image. The result must be " +
  "unmistakably a polished version of this same photograph, not a new or altered scene.";
